/**
 * CrossBrandPromo — reusable cross-sell widget between Stays ↔ Marketplace.
 *
 * Drops onto any page that wants to nudge a visitor toward the OTHER
 * brand. Reads the active brand via `useBrand()` and renders the
 * sibling-brand value prop, with a CTA href that points to:
 *   - the sibling's real domain (in production)
 *   - the same origin with `?brand=…` (in preview/dev)
 *
 * Usage:
 *   <CrossBrandPromo variant="card" />        // full card with image
 *   <CrossBrandPromo variant="strip" />       // horizontal strip
 *   <CrossBrandPromo variant="inline" />      // single sentence + link
 *
 * Override the default copy with `headline` / `description` / `ctaLabel`
 * props when a specific page wants to be more contextual (e.g. the
 * post-gift-card-purchase success page should say "Heading to Montreal
 * to redeem it?").
 */
import React from 'react';
import { ArrowRight, MapPin, Gift, Compass } from 'lucide-react';
import { useBrand } from '../brand/useBrand';
import { PALETTE_TOKENS } from '../brand/brandContext';

const NAVY = PALETTE_TOKENS.navy;
const PINE = PALETTE_TOKENS.pine;
const PINK = PALETTE_TOKENS.pink;

const HERO_IMG_STAYS =
  'https://customer-assets.emergentagent.com/job_07d157c1-ed6d-486a-862d-2e630b888519/artifacts/qhxzzv6h_%20-3.jpeg';

/**
 * Build the default copy strip for the CURRENT brand pointing at the
 * SIBLING brand. The marketplace ↔ stays pair has different value props
 * in each direction, so we resolve copy per-direction.
 */
const defaultCopy = (currentKey, siblingName) => {
  if (currentKey === 'marketplace') {
    return {
      eyebrow: 'Going to Montreal?',
      headline: `Book a stay at a ${siblingName} home.`,
      description:
        'Whole homes or by-the-room stays from local Montreal hosts. ' +
        'Pair a stay with your gift cards and we’ll credit your wallet.',
      ctaLabel: `Browse ${siblingName} stays`,
      // Stays landing for marketplace → stays direction
      targetPath: '/',
      icon: MapPin,
    };
  }
  // stays → marketplace (UCWonder): lead with the AI concierge. Gift
  // cards are a perk, not the pitch — the 3000+ brand access reads as
  // added-value discovery, not as a "we also sell gift cards" hook.
  return {
    eyebrow: 'More than a stay?',
    headline: `Meet ${siblingName} — your AI travel concierge.`,
    description:
      'One intelligent place for trip planning, curated experiences, ' +
      'and exclusive perks across 3000+ brands. Powered by an AI ' +
      'concierge that gets you.',
    ctaLabel: `Explore ${siblingName}`,
    targetPath: '/plan',
    icon: Gift,
  };
};


const CrossBrandPromo = ({
  variant = 'card',
  headline,
  description,
  ctaLabel,
  targetPath,
  eyebrow,
  // Allow callers to override the test id when multiple widgets render
  // on the same page (e.g. one in header strip, another in body).
  testId,
  // Optional: pass `false` to render WITHOUT the hero image (useful in
  // narrow side rails / drawers).
  showImage = true,
}) => {
  const { brand, brandKey, sibling, crossBrandHref } = useBrand();
  if (!sibling) return null;

  // Stays brand should never surface UCWonder cross-brand promos (user
  // decision 2026-07-03 — Homesbyli visitors shouldn't be aware of the
  // parent brand at all). UCWonder → Stays direction is preserved so
  // UCWonder can still promote Homesbyli. When a stronger separation
  // is needed later this early-return is the single kill switch.
  if (brandKey === 'stays') return null;

  const copy = defaultCopy(brandKey, sibling.displayName);
  const finalHeadline    = headline    ?? copy.headline;
  const finalDescription = description ?? copy.description;
  const finalCtaLabel    = ctaLabel    ?? copy.ctaLabel;
  const finalTargetPath  = targetPath  ?? copy.targetPath;
  const finalEyebrow     = eyebrow     ?? copy.eyebrow;
  const IconCmp          = copy.icon;

  const href = crossBrandHref(finalTargetPath);
  const tid = testId || `cross-brand-promo-${variant}-from-${brandKey}`;

  if (variant === 'inline') {
    return (
      <a
        href={href}
        className="inline-flex items-center gap-1.5 text-sm font-semibold hover:underline"
        style={{ color: PINE }}
        data-testid={tid}
      >
        <IconCmp className="w-4 h-4" strokeWidth={1.8} />
        {finalCtaLabel}
        <ArrowRight className="w-3.5 h-3.5" />
      </a>
    );
  }

  if (variant === 'strip') {
    return (
      <div
        className="rounded-2xl border border-gray-200 bg-white px-5 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
        data-testid={tid}
      >
        <div className="text-sm text-gray-700 min-w-0">
          <span className="font-semibold">{finalEyebrow}</span>{' '}
          <span className="text-gray-500">{finalDescription}</span>
        </div>
        <a
          href={href}
          className="inline-flex items-center justify-center gap-1.5 text-sm font-semibold rounded-full px-4 py-2 text-white hover:opacity-90 transition-opacity whitespace-nowrap"
          style={{ background: PINE }}
        >
          {finalCtaLabel}
          <ArrowRight className="w-3.5 h-3.5" />
        </a>
      </div>
    );
  }

  // variant === 'card' (default)
  return (
    <div
      className="rounded-3xl overflow-hidden"
      data-testid={tid}
      style={{
        background: brandKey === 'marketplace'
          ? `linear-gradient(135deg, ${NAVY}, ${PINE})`
          : `linear-gradient(135deg, ${PINE}, ${NAVY})`,
        color: '#fff',
      }}
    >
      <div className="grid lg:grid-cols-2 gap-6 lg:gap-8 items-center p-7 sm:p-10">
        <div>
          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] mb-3 opacity-80">
            <IconCmp className="w-3.5 h-3.5" strokeWidth={2} />
            {finalEyebrow}
          </div>
          <h3 className="text-2xl sm:text-3xl font-bold leading-tight">
            {finalHeadline}
          </h3>
          <p className="mt-3 text-sm sm:text-base opacity-90 max-w-md leading-relaxed">
            {finalDescription}
          </p>
          <a
            href={href}
            className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-opacity hover:opacity-90"
            style={{ backgroundColor: '#fff', color: PINE }}
          >
            {finalCtaLabel}
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>
        {showImage && (
          <div className="hidden lg:block">
            <div
              className="aspect-[4/3] rounded-2xl"
              style={brandKey === 'marketplace' ? {
                background: `linear-gradient(135deg, rgba(30,63,112,0.45), rgba(31,92,66,0.55)), url("${HERO_IMG_STAYS}")`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              } : {
                // Stays → marketplace: render a soft brand-mosaic placeholder
                background: `linear-gradient(135deg, ${PINK}40, ${NAVY}40)`,
              }}
            >
              {brandKey !== 'marketplace' && (
                <div className="w-full h-full flex items-center justify-center">
                  <Compass className="w-16 h-16 text-white opacity-70" strokeWidth={1.4} />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CrossBrandPromo;
